<template>
  <div
    :id="gfModalDataName"
    ref="gfModalWindow"
    class="gf-modal"
    :class="{ 'gf-modal --show': showModal }"
  >
    <div
      class="gf-modal__wrapper"
      :style="{
        '--gf-modal-wrapper-width': gfModalWrapperWidth,
        '--gf-modal-wrapper-height': gfModalWrapperHeight
      }"
    >
      <div
        v-if="gfModalHeader"
        class="gf-modal__header"
        :class="gfModalHeaderAdditionalClasses"
      >
        <!-- <h3 class="gf-modal__header-text">{{ gfModalHeader }}</h3> -->
        <component
          :is="computedHeaderLevel"
          class="gf-modal__header-text"
        >
          {{ gfModalHeader }}
        </component>
        <div class="gf-modal__header-border" />
      </div>
      <div
        class="gf-modal__body"
        :class="gfModalBodyAdditionalClasses"
      >
        <slot name="body" />
      </div>
      <div
        class="d-flex align-items-center gf-modal__close"
        @click="closeModalWindowOnClick"
      >
        <i class=" gf-icon gf-icon-x" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GfCustomModalWindow',
  props: {
    gfModalDataName: {
      required: true,
      default: null
    },
    gfModalHeader: { default: null },
    gfModalWrapperWidth: { default: 'auto' },
    gfModalWrapperHeight: { default: 'auto' },
    gfCloseModalButtonName: { default: null },
    headerLevel: {
      type: Number,
      default: 3
    },
    gfModalHeaderAdditionalClasses: {
      type: String,
      default: null
    },
    gfModalBodyAdditionalClasses: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      showModal: false,
      openModalButtons: null,
      closeModalButtons: null
    };
  },
  computed: {
    computedHeaderLevel() {
      switch (this.headerLevel) {
        case 1:
          return 'h1';
        case 2:
          return 'h2';
        case 3:
          return 'h3';
        case 4:
          return 'h4';
        case 5:
          return 'h5';
        case 6:
          return 'h6';
        default:
          return 3;
      }
    }
  },
  mounted() {
    this.openModalButtons = document.querySelectorAll(`[data-target="${this.gfModalDataName}"]`);
    if (this.openModalButtons) {
      this.openModalButtons.forEach((btn) => {
        btn.addEventListener('click', this.openModalOnClick);
      });
    }
    document.addEventListener('keydown', this.onKeydown);

    this.$refs.gfModalWindow.addEventListener('mousedown', this.closeModalWindowOnOverlayClick);

    if (this.gfCloseModalButtonName) {
      this.closeModalButtons = document.getElementsByName(this.gfCloseModalButtonName);
      this.closeModalButtons.forEach((btn) => {
        btn.addEventListener('click', this.closeModalWindow);
      });
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
    this.$refs.gfModalWindow.removeEventListener('mousedown', this.closeModalWindowOnOverlayClick);
    if (this.openModalButtons) {
      this.openModalButtons.forEach((btn) => {
        btn.removeEventListener('click', this.openModalOnClick);
      });
    }
    if (this.closeModalButtons) {
      this.closeModalButtons.forEach((btn) => {
        btn.removeEventListener('click', this.closeModalWindow);
      });
    }
  },
  methods: {
    openModalOnClick() {
      this.openModalWindow();
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        this.$emit('closeWithoutSubmit');
        this.closeModalWindow();
      }
    },
    openModalWindow() {
      this.$emit('open');
      this.showModal = true;
      document.querySelector('body').style.overflowY = 'hidden';
    },
    closeModalWindowOnOverlayClick(e) {
      if (e.target === this.$refs.gfModalWindow) {
        this.closeModalWindowOnClick();
      }
    },
    closeModalWindowOnClick() {
      this.$emit('closeWithoutSubmit');
      this.closeModalWindow();
    },
    closeModalWindow() {
      this.$emit('close');
      this.showModal = false;
      document.querySelector('body').style.overflowY = 'scroll';
    }
  }
};
</script>

<style lang="scss">
$this: '.gf-modal';

#{$this} {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  visibility: hidden;
  overflow: auto;
  backdrop-filter: blur(1px);
  background-color: transparent;
  z-index: 1050;
  pointer-events: none;
  transition: background-color 0.2s ease;
  will-change: background-color;

  #{$this}__wrapper {
    --gf-modal-wrapper-width: auto;
    --gf-modal-wrapper-height: auto;

    display: flex;
    padding: 16px 0;
    margin: auto;
    flex-direction: column;
    width: min(100dvw, var(--gf-modal-wrapper-width));
    height: min(100dvh, var(--gf-modal-wrapper-height));
    overflow-wrap: break-word;
    background-color: var(--light, #f8f9fa);
    border-radius: 16px;
    opacity: 0;
    transform: scale(0.9);
    transition: all 0.1s ease;
    will-change: transform, opacity;

    @media (max-width: 577px) {
      width: 100dvw;
      max-height: 90dvh;
      margin-bottom: 0;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      overflow-y: scroll;

      transform: translateY(100%);
      transition: all 0.3s cubic-bezier(0, 0.5, 0.25, 1) !important;
    }
  }

  #{$this}__header {
    &-text {
      margin: 0;
      padding: 20px 52px 20px 32px;
    }
    &-border {
      margin: 0 16px;
      border-bottom: 1px solid var(--border-color, #ced4da);
    }
  }

  #{$this}__body {
    padding: 16px 32px;
  }
  #{$this}__close {
    position: absolute;
    right: 0;
    padding: 10px 20px;
    cursor: pointer;
    font-size: 1.25rem;
    color: var(--text-muted, #6c757d);
    transition: color 0.2s ease;

    &:hover {
      color: var(--text, #000000);
    }
  }

  &.--show {
    padding: calc(100vw - 100%);
    display: flex;
    visibility: visible;
    pointer-events: all;
    background-color: #00000099;
    transition: background-color 0.3s ease;

    #{$this}__wrapper {
      opacity: 1;
      transform: scale(1);
      transition: opacity 0.15s ease, transform 0.15s ease;

      @media (max-width: 577px) {
        transform: translateY(0);
        transition: opacity 0.35s ease, transform 0.4s cubic-bezier(0, 0.5, 0.25, 1) !important;
      }
    }
  }
}
</style>
